/* eslint-disable no-nested-ternary */
import * as vp from 'vopay';

import { ChangeEvent, FunctionComponent, useEffect, useState } from 'react';
import styles from './TransferFund.module.scss';

const { Box, TextField, Icon, ErrorText, Radio, Button } = vp;

export type TransferFundClientAccountSelectProps = {
  variant: 'outlined' | 'standard' | 'plain' | 'chart' | 'background' | 'dropdown' | 'lined' | 'basic';
  options: { [key: string]: string };
  placeholder: string;
  name: string;
  value: string;
  dropDownValues: string;
  splitError?: string | null;
  transferType: string;
  error?: string | null;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  identifier?: string;
  setFieldValue: (field: string, value: string) => void;
  items?: { [key: string]: string };
  totalAmount?: string;
  currency?: string;
  bankAccounts?: {
    [idx: number]: vp.ClientBankAccountsLists;
  };
  disabled?: boolean;
};

export type MultiSelectDropdownItemProps = {
  ClientAccountID: string;
  Amount?: string;
  Percentage?: string;
};

type FC = FunctionComponent<TransferFundClientAccountSelectProps>;

const TransferFundClientAccountSelect: FC = ({
  value: input = '',
  dropDownValues,
  splitError,
  onBlur,
  onChange,
  totalAmount = '0',
  bankAccounts,
  disabled = false,
}: TransferFundClientAccountSelectProps) => {
  const [dropDownItems, setDropDownItems] = useState<MultiSelectDropdownItemProps[]>([]);
  const [splitMethod, setSplitMethod] = useState<string>('CustomSplit');
  const [isEqualSplit, setIsEqualSplit] = useState<boolean>(false);
  const [isUsingPercentage, setIsUsingPercentage] = useState<boolean>(false);
  const [clearSplitValues, setClearSplitValues] = useState<boolean>(false);
  const [, setClientBankAccounts] = useState<{
    [idx: number]: vp.ClientBankAccountsLists;
  }>([]);

  useEffect(() => {
    setIsUsingPercentage(dropDownItems.some((item) => item.Percentage && item.Percentage !== ''));

    if (clearSplitValues) {
      updateFields(JSON.stringify(filteredDropdownItems(dropDownItems)), 'RecipientClientAccountIDSplit');
      setClearSplitValues(false);
    }
  }, [dropDownItems]);

  useEffect(() => {
    if (dropDownValues !== '') {
      setDropDownItems(JSON.parse(dropDownValues));
    }
  }, []);

  useEffect(() => {
    if (bankAccounts) {
      setClientBankAccounts(bankAccounts);
    }
  }, [bankAccounts]);

  useEffect(() => {
    if (input.includes('|')) {
      const listOfClientAccountIDs = input.split('|');

      if (listOfClientAccountIDs.length !== dropDownItems.length) {
        setClearSplitValues(true);
      }

      const currentDropDownItems = createDropDownItems(listOfClientAccountIDs);
      setDropDownItems(currentDropDownItems);
      setSplitMethod('CustomSplit');
      setIsEqualSplit(false);
    } else {
      setDropDownItems([]);
    }
  }, [input]);

  function createDropDownItems(arr: string[]) {
    return arr.map((item) => ({
      ClientAccountID: item,
      Amount: '',
      Percentage: '',
    }));
  }

  function handleUpdateDropdownItem(index: number, field: string, event: React.ChangeEvent<HTMLInputElement>) {
    const updatedItems = [...dropDownItems];

    updatedItems[index] = {
      ...updatedItems[index],
      [field]: event.target.value,
    };

    setDropDownItems(updatedItems);

    // updateFields(createSelectedItems(updatedItems), 'RecipientClientAccountID');
    updateFields(JSON.stringify(filteredDropdownItems(updatedItems)), 'RecipientClientAccountIDSplit');
  }

  // This function is for updating RecipientClientAccountID and RecipientClientAccountIDSplit on some change
  function updateFields(value: string, name: string) {
    // Run synthetic event to update the fields
    const customEvent = {
      target: { value, name, checked: false },
    } as ChangeEvent<HTMLInputElement>;
    // Trigger the onChange event with the modified event object
    onChange(customEvent);
  }

  function filteredDropdownItems(items: MultiSelectDropdownItemProps[]) {
    return items.map((item) => {
      const newItem = { ...item };
      if (!item.Amount) {
        delete newItem.Amount;
      }
      if (!item.Percentage) {
        delete newItem.Percentage;
      }
      return newItem;
    });
  }

  function splitFundEqually() {
    if (parseFloat(totalAmount) > 0) {
      const recipientIds = input?.split('|') || [];
      const numRecipients = recipientIds.length;
      const amount = parseFloat(totalAmount);

      if (numRecipients > 1) {
        const numObjects = recipientIds.length;
        const individualAmount = (amount / numObjects).toFixed(2);
        const remainingAmount = (amount - parseFloat(individualAmount) * (numObjects - 1)).toFixed(2);

        const distributedDropDownItems = recipientIds.map((clientAccountID, index) => ({
          ClientAccountID: clientAccountID,
          Amount: index === numObjects - 1 ? remainingAmount : individualAmount,
          Percentage: '',
        }));

        setDropDownItems(distributedDropDownItems);
        updateFields(JSON.stringify(filteredDropdownItems(distributedDropDownItems)), 'RecipientClientAccountIDSplit');
      }
    }
  }

  function clearPercentage() {
    const updatedArray = dropDownItems.map((obj) => ({
      ...obj,
      Percentage: '',
    }));

    setDropDownItems(updatedArray);
  }

  useEffect(() => {
    isEqualSplit && parseFloat(totalAmount) > 0 && splitFundEqually();
  }, [totalAmount]);

  const handlers = {
    handleRadioButton: (values: string) => {
      setSplitMethod(values);
      if (values === 'EqualSplit') {
        setIsEqualSplit(true);
        splitFundEqually();
      } else {
        setIsEqualSplit(false);
        clearPercentage();
      }
    },
    handleRecalculateClick: () => {
      let totalAmount = 0;
      dropDownItems.forEach((item) => (totalAmount += parseFloat(item.Amount === '' ? '0' : (item.Amount ?? '0'))));
      updateFields(String(totalAmount), 'Amount');
    },
  };

  return (
    <Box flx="col">
      <Radio
        variant="inline"
        key="split-method-radio"
        name="split-method-radio"
        value={splitMethod}
        options={{
          CustomSplit: 'Custom Split',
          EqualSplit: 'Equal Split',
        }}
        noOrdering
        onBlur={() => {}}
        onChange={(e: { target: { value: string } }) => {
          handlers.handleRadioButton(e.target.value);
        }}
        setCheckedValue={() => {}}
        readOnly={disabled}
      />
      {dropDownItems.map((item, index) => (
        <div className={styles.dropDownItemContainer} key={index}>
          <div className={styles.dropDownItem} key={index}>
            <TextField
              variant="standard"
              name={`RecipientClientAccountIDSplit${index}`}
              value={item.ClientAccountID}
              onBlur={onBlur}
              onChange={onChange}
              disabled
              errorFitContent
            />
            <TextField
              name={`RecipientClientAccountIDSplitAmount${index}`}
              variant="standard"
              value={item.Amount}
              placeholder="0.00"
              elementLeft={<Icon icon="dollar" size="xsm" color="grey" />}
              disabled={isEqualSplit || isUsingPercentage}
              onBlur={onBlur}
              onChange={(e) => handleUpdateDropdownItem(index, 'Amount', e)}
              height="37px"
            />
            {!isEqualSplit && (
              <>
                <div>or</div>
                <TextField
                  name={`RecipientClientAccountIDSplitPercentage${index}`}
                  variant="standard"
                  value={item.Percentage}
                  placeholder="0.00"
                  elementLeft={<Icon icon="percentage" size="xsm" color="grey" />}
                  disabled={dropDownItems.some((item) => item.Amount && item.Amount !== '')}
                  onBlur={onBlur}
                  onChange={(e) => handleUpdateDropdownItem(index, 'Percentage', e)}
                  height="37px"
                />
              </>
            )}
          </div>
        </div>
      ))}

      <Box>
        <Box p="md">
          <ErrorText error={splitError} size="sm" fitContent />
        </Box>
        {splitError === 'Accumulated amount cannot be greater than total amount' && (
          <Box p="smd">
            <Button
              id="recalculate-total-amount"
              color="none"
              size="sm"
              background="none"
              borderColor="none"
              bold
              textDecoration="underline"
              label="Recalculate Total Amount"
              onClick={() => {
                handlers.handleRecalculateClick();
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TransferFundClientAccountSelect;
